import * as Tone from 'tone';
import { Chord, ChordProgression } from '../types';
import Malakai, { MalakaiConfig } from '../Malakai';
import { lerp } from '../math-utils';
import { GenerationMode } from '../generators/BaseGenerator';
import * as INSTRUMENTS from '../catalog/instruments';
import LEADS from '../catalog/leads';
import { MusicVAEGenerator } from '../generators';

const TEMPO = 60;
const CHORD_BASE_VELOCITY = 0.1;

let startProgression!: ChordProgression;
let middleProgression!: ChordProgression;
let endProgression!: ChordProgression;

function getCurrentProgression(progress: number): ChordProgression {
  if (progress < 0.33) {
    return startProgression;
  }
  if (progress < 0.66) {
    return middleProgression;
  }
  return endProgression;
}

export default <MalakaiConfig>{
  name: 'Yoga Influencer',
  description: '100% GMO-free organic beats',
  tempo: TEMPO,
  duration: '24m',
  quantizeCurve: true,
  sliders: [
    {
      key: 'electronic',
      name: 'Organic/Electronic',
      description: 'Pretty self explanatory I think.',
      value: 0.5,
    },
    {
      key: 'temperature',
      name: 'Generator Temperature',
      description: 'Randomness of generator.',
      value: 0.5,
      min: 0,
      max: 2,
    },
    {
      key: 'similarity',
      name: 'Generator Similarity',
      description: 'Similarity of generated melody.',
      value: 0.9,
    },
  ],
  generators: [
    {
      name: 'melody',
      lead: LEADS.GirlFutureLow,
      generator: new MusicVAEGenerator({
        // https://github.com/magenta/magenta-js/blob/master/music/checkpoints/README.md
        checkpoint: 'https://storage.googleapis.com/magentadata/js/checkpoints/music_vae/mel_chords',
        mode: GenerationMode.Looping,
        tempo: TEMPO,
        sequenceDuration: '4m',
        lookbackDuration: '4m',
        conditionGenerator: (malakai: Malakai, progress) => ({
          samples: 8,
          similarity: malakai.getSliderValue('similarity'),
          temperature: malakai.getSliderValue('temperature'),
          chordProgression: getCurrentProgression(progress),
          key: 0,
        }),
      }),
    },
  ],
  layers: [
    {
      name: 'control',
      events: [
        {
          start: '8m',
          callback({ malakai, layer }, time) {
            malakai.setLayerActive('chord_secondary', true);
          },
        },
        {
          start: '16m',
          callback({ malakai, layer }, time) {
            malakai.setLayerActive('pad_secondary', true);
          },
        },
        {
          start: 0,
          callback({ malakai, layer }, time) {
            malakai.setLayerActive('melody2', false);
          },
        },
        {
          start: '8m',
          callback({ malakai, layer }, time) {
            malakai.setLayerActive('melody2', true);
          },
        },
        {
          start: '8m',
          callback({ malakai, layer }, time) {
            malakai.setLayerActive('melody', false);
          },

        },
        {
          start: '16m',
          callback({ malakai, layer }, time) {
            malakai.setLayerActive('melody', true);
          },
        },
      ],
    },
    {
      name: 'chord_bass',
      instruments: {
        bass: INSTRUMENTS.BassCleanPickedFender,
      },
      loops: [
        {
          start: '8m',
          interval: '8m',
          callback({ transport, layer }, time) {
            const progress = transport.getProgressForTime(time);
            const progression = getCurrentProgression(progress);
            const part = new Tone.Part((t, chord: Chord) => {
              const chordDuration = Tone.Time(chord.duration).toSeconds();
              layer.instruments.bass.triggerAttackRelease(
                chord.notes[0],
                chordDuration,
                transport.secondsToTransportTime(t),
                chord.velocity * CHORD_BASE_VELOCITY,
              );
            }, progression.chords);
            part.loop = 2;
            part.loopStart = 0;
            part.loopEnd = '4m';
            part.start(time - transport.lookAhead);
          },
        },
      ],
    },
    {
      name: 'chord_bass2',
      instruments: {
        bass2: INSTRUMENTS.KeysHangDrumEcho,
      },
      loops: [
        {
          start: '8m',
          interval: '8m',
          callback({ transport, layer }, time) {
            const progress = transport.getProgressForTime(time);
            const progression = getCurrentProgression(progress);
            const part = new Tone.Part((t, chord: Chord) => {
              const chordDuration = Tone.Time(chord.duration).toSeconds();
              layer.instruments.bass2.triggerAttackRelease(
                chord.notes[0],
                chordDuration,
                transport.secondsToTransportTime(t),
                chord.velocity * CHORD_BASE_VELOCITY,
              );
            }, progression.chords);
            part.loop = 2;
            part.loopStart = 0;
            part.loopEnd = '4m';
            part.start(time - transport.lookAhead);
          },
        },
      ],
    },
    {
      name: 'chord_main',
      instruments: {
        main: INSTRUMENTS.PadMorningDreamsSynth,
      },
      loops: [
        {
          start: '8m',
          interval: '8m',
          callback({ transport, layer }, time) {
            const progress = transport.getProgressForTime(time);
            const progression = getCurrentProgression(progress);
            const part = new Tone.Part((t, chord: Chord) => {
              const chordDuration = Tone.Time(chord.duration).toSeconds();
              chord.notes.slice(1).forEach((note) => {
                layer.instruments.main.triggerAttackRelease(
                  note,
                  chordDuration,
                  transport.secondsToTransportTime(t),
                  chord.velocity * CHORD_BASE_VELOCITY,
                );
              });
            }, progression.chords);
            part.loop = 2;
            part.loopStart = 0;
            part.loopEnd = '4m';
            part.start(time - transport.lookAhead);
          },
        },
      ],
    },
    {
      name: 'chord_secondary',
      instruments: {
        secondary: INSTRUMENTS.KeysHangDrumEcho,
      },
      loops: [
        {
          start: 0,
          interval: '8m',
          callback({ transport, layer }, time) {
            const progress = transport.getProgressForTime(time);
            const progression = getCurrentProgression(progress);
            const part = new Tone.Part((t, chord: Chord) => {
              const chordDuration = Tone.Time(chord.duration).toSeconds();
              chord.notes.forEach((note) => {
                layer.instruments.secondary.triggerAttackRelease(
                  note,
                  chordDuration,
                  transport.secondsToTransportTime(t),
                  chord.velocity * CHORD_BASE_VELOCITY,
                );
              });
            }, progression.chords);
            part.loop = 2;
            part.loopStart = 0;
            part.loopEnd = '4m';
            part.start(time - transport.lookAhead);
          },
        },
      ],
    },
    {
      name: 'pad',
      instruments: {
        pad: INSTRUMENTS.PadWindyGuitaret,
      },
      loops: [
        {
          start: 0,
          interval: '8m',
          callback({ transport, layer }, time) {
            const progress = transport.getProgressForTime(time);
            const progression = getCurrentProgression(progress);
            const part = new Tone.Part((t, chord: Chord) => {
              const chordDuration = Tone.Time(chord.duration).toSeconds();
              chord.notes.slice(1).forEach((note) => {
                layer.instruments.pad.triggerAttackRelease(
                  note,
                  chordDuration,
                  transport.secondsToTransportTime(t),
                  chord.velocity * CHORD_BASE_VELOCITY,
                );
              });
            }, progression.chords);
            part.loop = 2;
            part.loopStart = 0;
            part.loopEnd = '4m';
            part.start(time - transport.lookAhead);
          },
        },
      ],
    },
    {
      name: 'pad_secondary',
      instruments: {
        pad: INSTRUMENTS.DirtyAirPad,
      },
      loops: [
        {
          start: '16m',
          interval: '8m',
          callback({ transport, layer }, time) {
            const progress = transport.getProgressForTime(time);
            const progression = getCurrentProgression(progress);
            const part = new Tone.Part((t, chord: Chord) => {
              const chordDuration = Tone.Time(chord.duration).toSeconds();
              chord.notes.slice(1).forEach((note) => {
                layer.instruments.pad.triggerAttackRelease(
                  note,
                  chordDuration,
                  transport.secondsToTransportTime(t),
                  chord.velocity * CHORD_BASE_VELOCITY,
                );
              });
            }, progression.chords);
            part.loop = 2;
            part.loopStart = 0;
            part.loopEnd = '4m';
            part.start(time - transport.lookAhead);
          },
        },
      ],
    },
    {
      name: 'sparkle',
      instruments: {
        spark: INSTRUMENTS.SparkEternalVox,
      },
      loops: [
        {
          start: 0,
          interval: '8m',
          callback({ transport, layer }, time) {
            const progress = transport.getProgressForTime(time);
            const progression = getCurrentProgression(progress);
            const part = new Tone.Part((t, chord: Chord) => {
              const chordDuration = Tone.Time(chord.duration).toSeconds();
              layer.instruments.spark.triggerAttackRelease(
                chord.notes[2],
                chordDuration,
                transport.secondsToTransportTime(t),
                chord.velocity * CHORD_BASE_VELOCITY,
              );
            }, progression.chords);
            part.loop = 2;
            part.loopStart = 0;
            part.loopEnd = '4m';
            part.start(time - transport.lookAhead);
          },
        },
      ],
    },
    {
      name: 'melody',
      generator: 'melody',
      instruments: {
        lead1: INSTRUMENTS.SynthKalimba,
      },
      hooks: {
        note({ malakai, layer }, event) {
          const { time, frequency, duration } = event;
          if (time && frequency && duration) {
            const curveY = layer.curve.getValue(time);
            const velocity = lerp(0.5, 0.5, curveY);
            layer.instruments.lead1.triggerAttackRelease(
              frequency,
              duration,
              time,
              velocity,
            );
          }
        },
      },
    },
    {
      name: 'melody2',
      disabled: true,
      generator: 'melody',
      instruments: {
        lead2: INSTRUMENTS.MarimbaSynth,
      },
      hooks: {
        note({ malakai, layer }, event) {
          const { time, frequency, duration } = event;
          if (time && frequency && duration) {
            const curveY = layer.curve.getValue(time);
            const velocity = lerp(0.5, 0.5, curveY);
            layer.instruments.lead2.triggerAttackRelease(
              frequency,
              duration,
              time,
              velocity,
            );
          }
        },
      },
    },
  ],
  hooks: {
    preStart({ malakai }) {
      startProgression = malakai.findChordProgression(malakai.curve.getValue(0));
      middleProgression = malakai.findChordProgression(malakai.curve.getValue(0.33));
      endProgression = malakai.findChordProgression(malakai.curve.getValue(0.66));
    },
  },
};
